import React from "react";
import { motion } from "framer-motion";
import { Terminal, Server, Database, Code, Bot } from "lucide-react";

const AboutSection = () => {
  // Tech stacks grouped by category
  const techStacks = {
    backend: [
      "Python",
      "FastAPI",
      "Django",
      "Java",
      "Spring Boot",

      "REST API",
      "GraphQL",
    ],
    database: [
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "Redis",
      "Supabase",
      "AWS RDS",
    ],
    ai: [
      "LangChain",
      "LLMs",
      "RAG",
      "AWS AI Services",
      "Vector Databases",
      "n8n",
      "Make",
    ],
    frontend: ["React Js", "React Native", "Tailwind CSS", "TypeScript"],
    devops: [
      "AWS",
      "Docker",
      "CI/CD",
      "Heroku",
      "Serverless",
      "Kafka",
      "RabbitMQ",
    ],
  };

  // Function to render tech stack tags
  const renderTechTags = (tags) => {
    return tags.map((tag, index) => (
      <span
        key={index}
        className="px-3 py-1.5 bg-gray-100 text-gray-800 text-xs font-medium rounded-full"
      >
        {tag}
      </span>
    ));
  };

  return (
    <section id="about" className="py-24 mt-16 mb-16 bg-white rounded-2xl">
      <div className="w-full max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-2 font-mono">
              About Me
            </h2>
            <div className="w-16 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="max-w-2xl mx-auto text-gray-600">
              Developer, problem solver, and technology enthusiast building
              solutions at the intersection of AI and software
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Photo Section - 2 columns on large screens */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="relative h-full">
              <div className="rounded-2xl overflow-hidden h-full shadow-md">
                {/* Replace with your actual photo */}
                <img
                  src="/tresorkl.jpeg"
                  alt="Tresor Kwabezagwa"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://via.placeholder.com/600x800?text=Tresor+KL";
                  }}
                />
              </div>

              {/* Decorative Elements */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-blue-100 -z-10"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-blue-100 -z-10"></div>
            </div>
          </motion.div>

          {/* Content Section - 3 columns on large screens */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold font-mono mb-4 flex items-center">
                  <Terminal className="w-6 h-6 mr-2 text-blue-600" />
                  <span>Who I Am</span>
                </h3>

                <div className="space-y-4 text-gray-600">
                  <p>
                    I'm a backend-focused developer with a strong Computer
                    Science foundation from Tshwane University of Technology. My
                    passion lies in building robust, scalable systems that solve
                    real business problems through the combination of
                    traditional software engineering and modern AI capabilities.
                  </p>

                  <p>
                    As the founder of{" "}
                    <span className="font-semibold text-blue-600">
                      AAAGentive
                    </span>
                    , I create AI-powered automation solutions for businesses
                    across South Africa, with a focus on the real estate and
                    hospitality sectors. I specialize in conversational AI
                    agents that transform how businesses operate and interact
                    with their customers.
                  </p>

                  <p>
                    My approach combines technical expertise with business
                    understanding, allowing me to deliver solutions that not
                    only work technically but create measurable business impact.
                    Whether it's building high-performance backends, developing
                    mobile applications, or implementing AI automation, I focus
                    on creating systems that are maintainable, scalable, and
                    solve the core business challenge.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold font-mono mb-4 flex items-center">
                  <Code className="w-6 h-6 mr-2 text-blue-600" />
                  <span>What I Do</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 rounded-full p-2 mr-3">
                        <Server className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="font-bold">Backend Development</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Building robust, scalable APIs and services using Python
                      and Java ecosystems, with a focus on performance and
                      security.
                    </p>
                  </div>

                  <div className="rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 rounded-full p-2 mr-3">
                        <Bot className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="font-bold">AI & Automation</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Creating intelligent AI agents and custom automation
                      workflows that transform how businesses operate and serve
                      customers.
                    </p>
                  </div>

                  <div className="rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 rounded-full p-2 mr-3">
                        <Database className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="font-bold">Data Engineering</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Designing and optimizing database structures, building
                      data processing pipelines, and implementing efficient data
                      retrieval systems.
                    </p>
                  </div>

                  <div className="rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 rounded-full p-2 mr-3">
                        <svg
                          className="w-5 h-5 text-blue-600"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 2V4M12 20V22M4 12H2M6.31412 6.31412L4.8999 4.8999M17.6859 6.31412L19.1001 4.8999M6.31412 17.69L4.8999 19.1042M17.6859 17.69L19.1001 19.1042M22 12H20"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <h4 className="font-bold">Full-Stack Development</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Developing end-to-end web and mobile applications with
                      modern user interfaces and powerful backend systems.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold font-mono mb-4 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-blue-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 22V12H15V22M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Technologies I Use</span>
                </h3>

                <div className="space-y-5">
                  <div>
                    <h4 className="text-sm uppercase tracking-wider text-gray-500 mb-2 font-mono">
                      Backend & API
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {renderTechTags(techStacks.backend)}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm uppercase tracking-wider text-gray-500 mb-2 font-mono">
                      Database & Storage
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {renderTechTags(techStacks.database)}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm uppercase tracking-wider text-gray-500 mb-2 font-mono">
                      AI & Automation
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {renderTechTags(techStacks.ai)}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm uppercase tracking-wider text-gray-500 mb-2 font-mono">
                      Frontend & Mobile
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {renderTechTags(techStacks.frontend)}
                    </div>
                  </div>

                  {/* <div>
                    <h4 className="text-sm uppercase tracking-wider text-gray-500 mb-2 font-mono">
                      DevOps & Cloud
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {renderTechTags(techStacks.devops)}
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
